import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FooterNew from "../components/Footer-New";
import NavbarNew from "../components/Navbar-New.js";


export default function RealEstate() {
  const [activeSection, setActiveSection] = useState("intro");

  const sections = [
    { id: "intro", title: "Introduction" },
    { id: "what-is", title: "What Is Long-Term Portfolio Construction?" },
    { id: "key-benefits", title: "Key Benefits of Real Estate" },
    { id: "comparison", title: "Real Estate vs. Other Asset Classes" },
    { id: "allocation", title: "Portfolio Allocation" },
    { id: "ways-to-invest", title: "Ways to Invest" },
    { id: "mistakes", title: "Common Mistakes to Avoid" },
    { id: "timing", title: "Is Now a Good Time?" },
    { id: "next-step", title: "Take the First Step" },
  ];

  const scrollToSection = (e, id) => {
    e.preventDefault();
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div className="bg-[#f4f3f3] min-h-screen">

      <NavbarNew />

      <div className="w-full px-4 sm:px-6 md:px-8  py-8 sm:py-12   2xl:py-[80px]">
        <div className="max-w-[1440px] mx-auto flex flex-col lg:flex-row justify-between items-start gap-12 2xl:gap-16">

          {/* Left Content Area */}
          <div className="w-full lg:w-2/3 xl:w-3/4 flex flex-col gap-8 bg-white p-6 md:p-10 lg:p-14 rounded-lg shadow-sm border border-[#395645]/10">

            {/* Main Title */}
            <h1 className="text-[#395645] text-3xl sm:text-4xl lg:text-5xl xl:text-[56px]  font-normal font-['Forum'] ">
              The Role of Real Estate in Long-Term Portfolio Construction
            </h1>

            {/* Hero Image */}
            <div className="w-full  overflow-hidden rounded-sm md:rounded-lg">
              <img
                src={require("../assets/images/home/service_05.png")}
                alt="Real Estate Portfolio"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Body Content */}
            <div className="text-[#3a4f66] text-base md:text-lg font-normal font-['Roobert'] leading-relaxed flex flex-col gap-6">
              <p className="font-semibold text-xl">
                Discover how real estate can strengthen your long-term investment portfolio. Learn the key benefits, strategies, and tips investors need to build lasting wealth through property.
              </p>

              {/* Sections */}
              <div id="intro" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-4">Introduction: Why Real Estate Belongs in Your Investment Portfolio</h3>
                <p className="mb-4">If you're just starting your investment journey, you've probably heard that diversification is key. But what does a truly diversified portfolio look like and where does real estate fit in?</p>
                <p className="mb-4">For decades, real estate has been one of the most reliable wealth-building tools available to everyday investors. Unlike stocks or bonds, property offers a unique combination of income generation, capital appreciation, and inflation protection making it a powerful cornerstone of any long-term portfolio.</p>
                <p>In this guide, we'll walk you through exactly why real estate deserves a place in your portfolio, how it compares to other asset classes, and how you can get started.</p>
              </div>

              <div id="what-is" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">What Is Long-Term Portfolio Construction?</h3>
                <p className="mb-4">Long-term portfolio construction is the process of strategically selecting and managing a mix of investments such as stocks, bonds, cash, and alternative assets designed to grow your wealth over many years or decades.</p>
                <p className="mb-4">The goal isn't to get rich overnight. It's to build a resilient, diversified portfolio that can weather market volatility, generate consistent returns, and help you achieve your financial goals over time.</p>
                <p>Real estate is widely considered one of the best alternative assets to include in this mix.</p>
              </div>

              <div id="key-benefits" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Key Benefits of Real Estate in a Long-Term Portfolio</h3>

                <h4 className="text-[#395645] text-xl font-semibold mt-4 mb-2">1. Consistent Income Through Rental Yields</h4>
                <p className="mb-4">One of the most attractive features of real estate is its ability to generate passive income. When you own a rental property, tenants pay you rent each month, creating a steady cash flow that can supplement your income or be reinvested to grow your portfolio faster.</p>
                <p className="mb-6">Unlike dividends from stocks, rental income tends to be more predictable and less volatile, making it especially appealing for beginner investors looking for stability.</p>

                <h4 className="text-[#395645] text-xl font-semibold mb-2">2. Capital Appreciation Over Time</h4>
                <p className="mb-4">Historically, property values have increased over the long term. While short-term fluctuations exist, real estate in well-located areas tends to appreciate steadily, meaning your asset grows in value the longer you hold it.</p>
                <p className="mb-6">This dual return income plus appreciation is what makes real estate particularly powerful for long-term wealth building.</p>

                <h4 className="text-[#395645] text-xl font-semibold mb-2">3. A Natural Hedge Against Inflation</h4>
                <p className="mb-6">Inflation erodes the purchasing power of cash and can negatively impact bonds. Real estate, however, tends to move in line with inflation or even outpace it. As the cost of living rises, so do property values and rental income, helping protect the real value of your investment.</p>

                <h4 className="text-[#395645] text-xl font-semibold mb-2">4. Portfolio Diversification and Reduced Risk</h4>
                <p className="mb-4">Real estate often has a low correlation with stock markets. This means when equities fall, property values don't necessarily follow. Adding real estate to your portfolio can reduce overall volatility and provide a buffer during market downturns.</p>
                <p className="mb-6">For investors, this diversification is one of the most important risk-management tools available.</p>

                <h4 className="text-[#395645] text-xl font-semibold mb-2">5. Leverage: Amplifying Your Returns</h4>
                <p>Real estate is one of the few asset classes where you can use leverage borrowing money (via a mortgage) to control a larger asset than you could with cash alone. This means a relatively small initial investment can control a much larger asset, amplifying your potential returns over time.</p>
              </div>

              <div id="comparison" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Real Estate vs. Other Asset Classes</h3>
                <div className="overflow-x-auto mt-4 mb-6">
                  <table className="min-w-full text-left border-collapse border border-[#395645]/20">
                    <thead className="bg-[#395645]/5">
                      <tr>
                        <th className="p-3 border border-[#395645]/20 font-semibold text-[#395645]">Feature</th>
                        <th className="p-3 border border-[#395645]/20 font-semibold text-[#395645]">Real Estate</th>
                        <th className="p-3 border border-[#395645]/20 font-semibold text-[#395645]">Stocks</th>
                        <th className="p-3 border border-[#395645]/20 font-semibold text-[#395645]">Bonds</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="p-3 border border-[#395645]/20 font-medium">Income Generation</td>
                        <td className="p-3 border border-[#395645]/20">Rental yield</td>
                        <td className="p-3 border border-[#395645]/20">Dividends</td>
                        <td className="p-3 border border-[#395645]/20">Interest payments</td>
                      </tr>
                      <tr className="bg-[#f4f3f3]">
                        <td className="p-3 border border-[#395645]/20 font-medium">Capital Growth</td>
                        <td className="p-3 border border-[#395645]/20">High (long-term)</td>
                        <td className="p-3 border border-[#395645]/20">High (variable)</td>
                        <td className="p-3 border border-[#395645]/20">Low</td>
                      </tr>
                      <tr className="bg-white">
                        <td className="p-3 border border-[#395645]/20 font-medium">Inflation Protection</td>
                        <td className="p-3 border border-[#395645]/20">Strong</td>
                        <td className="p-3 border border-[#395645]/20">Moderate</td>
                        <td className="p-3 border border-[#395645]/20">Weak</td>
                      </tr>
                      <tr className="bg-[#f4f3f3]">
                        <td className="p-3 border border-[#395645]/20 font-medium">Volatility</td>
                        <td className="p-3 border border-[#395645]/20">Low–Moderate</td>
                        <td className="p-3 border border-[#395645]/20">High</td>
                        <td className="p-3 border border-[#395645]/20">Low</td>
                      </tr>
                      <tr className="bg-white">
                        <td className="p-3 border border-[#395645]/20 font-medium">Leverage Potential</td>
                        <td className="p-3 border border-[#395645]/20">High</td>
                        <td className="p-3 border border-[#395645]/20">Low</td>
                        <td className="p-3 border border-[#395645]/20">Low</td>
                      </tr>
                      <tr className="bg-[#f4f3f3]">
                        <td className="p-3 border border-[#395645]/20 font-medium">Liquidity</td>
                        <td className="p-3 border border-[#395645]/20">Low</td>
                        <td className="p-3 border border-[#395645]/20">High</td>
                        <td className="p-3 border border-[#395645]/20">Moderate</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p>As the table shows, real estate isn't perfect for every situation, its lower liquidity means it's best suited for long-term investors who don't need immediate access to their capital.</p>
              </div>

              <div id="allocation" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">How Much of Your Portfolio Should Be in Real Estate?</h3>
                <p className="mb-4">While there's no one-size-fits-all answer, many financial experts suggest allocating 15% to 30% of a long-term portfolio to real estate, depending on your:</p>
                <ul className="list-disc pl-5 mb-4 flex flex-col gap-2">
                  <li>Investment goals (income vs. growth)</li>
                  <li>Risk tolerance</li>
                  <li>Time horizon</li>
                  <li>Available capital</li>
                </ul>
                <p>As a beginner investor, starting with a single buy-to-let property or a Real Estate Investment Trust (REIT) can be a practical and manageable entry point.</p>
              </div>

              <div id="ways-to-invest" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Ways to Invest in Real Estate</h3>

                <h4 className="text-[#395645] text-xl font-semibold mt-4 mb-2">Direct Property Investment</h4>
                <p className="mb-4">Buying a residential or commercial property outright (or via a mortgage) is the most traditional route. It gives you full control but requires significant capital and active management.</p>

                <h4 className="text-[#395645] text-xl font-semibold mb-2">Real Estate Investment Trusts (REITs)</h4>
                <p className="mb-4">REITs allow you to invest in a portfolio of properties without owning physical real estate. They're traded like stocks, making them highly accessible, liquid, and ideal for beginners with limited capital.</p>

                <h4 className="text-[#395645] text-xl font-semibold mb-2">Property Crowdfunding Platforms</h4>
                <p className="mb-4">Online platforms allow multiple investors to pool funds and invest in larger property projects together. This lowers the barrier to entry and spreads risk across multiple properties.</p>

                <h4 className="text-[#395645] text-xl font-semibold mb-2">Buy-to-Let Properties</h4>
                <p>A popular strategy in the UK, buy-to-let involves purchasing a property specifically to rent it out. With the right location and tenant management, it can deliver both rental income and long-term capital growth.</p>
              </div>

              <div id="mistakes" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Common Mistakes Investors Should Avoid</h3>
                <ul className="list-disc pl-5 mb-4 flex flex-col gap-2">
                  <li><strong>Ignoring location:</strong> Property value and rental demand are heavily influenced by location. Always research the local market thoroughly.</li>
                  <li><strong>Underestimating costs:</strong> Factor in maintenance, insurance, property management fees, and potential void periods when calculating returns.</li>
                  <li><strong>Over-leveraging:</strong> Borrowing too much increases your risk exposure. Keep debt at a manageable level.</li>
                  <li><strong>Thinking short-term:</strong> Real estate rewards patience. Avoid making emotional decisions based on short-term market noise.</li>
                  <li><strong>Skipping professional advice:</strong> Tax, legal, and financial considerations in real estate can be complex. Always seek qualified guidance before investing.</li>
                </ul>
              </div>

              <div id="timing" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Is Now a Good Time to Invest in Real Estate?</h3>
                <p className="mb-4">Markets fluctuate, and timing is never perfect. However, long-term investors know that time in the market generally beats timing the market. If you have a 10, 20, or 30-year horizon, the short-term dips matter far less than building a solid, diversified portfolio today.</p>
                <p>The key is to start informed, start strategic and start with the right support.</p>
              </div>

              <div id="next-step" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Take the First Step Toward Building Your Property Portfolio</h3>
                <p className="mb-4">Real estate can be one of the most rewarding additions to a long-term investment portfolio — but navigating the market as a beginner can feel overwhelming. That's where we come in.</p>
                <p className="mb-4">Our team of experienced real estate investment specialists can help you:</p>
                <ul className="list-disc pl-5 mb-6 flex flex-col gap-2">
                  <li>Identify the right property opportunities for your goals</li>
                  <li>Understand the financial and tax implications</li>
                  <li>Build a long-term strategy tailored to your situation</li>
                </ul>
                <p className="font-semibold text-lg text-[#395645] flex items-start gap-2">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-1">
                    <path fillRule="evenodd" clipRule="evenodd" d="M4.81875 9.1075L6.1525 8.44C6.38929 8.32184 6.58248 8.13157 6.70423 7.89661C6.82598 7.66164 6.87002 7.39409 6.83 7.1325L6.28 3.56C6.23463 3.26496 6.08516 2.99589 5.85865 2.80147C5.63213 2.60705 5.34351 2.50011 5.045 2.5H3.75C3.41848 2.5 3.10054 2.6317 2.86612 2.86612C2.6317 3.10054 2.5 3.41848 2.5 3.75V6.25C2.5 7.13625 2.6025 8 2.7975 8.8275C3.27793 10.8636 4.31594 12.7256 5.79519 14.2048C7.27443 15.6841 9.13644 16.7221 11.1725 17.2025C12.0173 17.4007 12.8822 17.5006 13.75 17.5H16.25C16.5815 17.5 16.8995 17.3683 17.1339 17.1339C17.3683 16.8995 17.5 16.5815 17.5 16.25V14.955C17.4999 14.6565 17.3929 14.3679 17.1985 14.1414C17.0041 13.9148 16.735 13.7654 16.44 13.72L12.8675 13.17C12.6059 13.13 12.3384 13.174 12.1034 13.2958C11.8684 13.4175 11.6782 13.6107 11.56 13.8475L10.8925 15.1813C9.47011 14.7245 8.17708 13.9352 7.12059 12.8789C6.0641 11.8227 5.27584 10.5298 4.81875 9.1075Z" fill="#395645" />
                  </svg>
                  <span>
                    Ready to get started? <Link to="/contact" className="underline hover:text-[#395645]/80 transition-colors">Book a free consultation</Link> today and take the first step toward building lasting wealth through real estate.
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Table of Contents */}
          <div className="w-full lg:w-1/3 xl:w-1/4 hidden lg:block sticky top-[120px]">
            <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm border border-[#395645]/10">

              <div className="flex items-center gap-2 mb-6 border-b border-[#395645]/10 pb-4">
                {/* <div className="w-2 h-2 bg-[#395645] rounded-full" /> */}
                <h3 className="text-[#395645] text-xl sm:text-2xl font-['Forum']">Table of Contents</h3>

              </div>
              <ul className="flex flex-col gap-3">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={(e) => scrollToSection(e, s.id)}
                      className={`block py-1 text-sm sm:text-[15px] font-['Roobert'] transition-all duration-300 border-l-2 pl-4 ${activeSection === s.id
                          ? 'border-[#395645] text-[#395645] font-semibold bg-[#395645]/5'
                          : 'border-transparent text-[#3a4f66]/70 hover:text-[#395645] hover:border-[#395645]/30'
                        }`}
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      </div>

      <FooterNew />
    </div>
  );
}
